import {resolve} from "../../container";
import {QueryParameters} from "../../common/queryParameters";
import {html, LitElement, type TemplateResult} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {EopOverlaySpinner} from "../../page/elements/spinner";
import {GLOBAL} from "../../common/globals";
import {EOP_ERROR_HANDLER, schedule} from "../../common/utils/promises";
import {type DOIResponse, MacherbusService} from "./macherbusService";
import type {Nullable} from "../../bootstrap/common/types";

const VOTECODE_QUERY_PARAM = "optinid";
const ACTION_QUERY_PARAM = "action";

const validActions = ["confirm", "reject", "deadline-error", "error"] as const;
type Action = (typeof validActions)[number];

@customElement("eop-macherbus-followup")
export class EopMacherbusFollowUp extends LitElement {

    @state()
    private actionState: Nullable<Action>;
    @property({attribute: "voting-id"})
    private votingId: string;
    @property({attribute: "language"})
    private language: string;

    private action: Nullable<Action>;
    private voteCode: Nullable<string>;
    private spinner: EopOverlaySpinner;

    public constructor(private macherbusService: MacherbusService = resolve(MacherbusService),
                       private queryParameters: QueryParameters = resolve(QueryParameters)) {
        super();
        this.actionState = null;
        this.action = this.validateAction(this.queryParameters.getString(ACTION_QUERY_PARAM));
        this.voteCode = this.queryParameters.getString(VOTECODE_QUERY_PARAM);
        this.spinner = new EopOverlaySpinner();
    }

    private validateAction(action: Nullable<string>): Nullable<Action> {
        if (action === null) {
            return null;
        }
        return (validActions as unknown as string[]).includes(action)
            ? action as Action
            : "error";
    }

    public connectedCallback(): void {
        super.connectedCallback();
        GLOBAL.bodyElement().append(this.spinner);
        if (this.voteCode && this.action) {
            void this.actionRequest(this.voteCode);
        }
    }

    public disconnectedCallback(): void {
        this.spinner.remove();
        super.disconnectedCallback();
    }

    public render(): Nullable<TemplateResult> {
        if (!this.actionState) {
            return null;
        }

        return html`
            <slot name="${this.actionState}-text"></slot>
        `;
    }

    private async actionRequest(voteCode: string): Promise<void> {
        let request: Promise<DOIResponse>;

        if (this.action === "confirm") {
            request = this.macherbusService.confirm({votingId: this.votingId, lang: this.language, voteCode: voteCode});
        } else if (this.action === "reject") {
            request = this.macherbusService.reject({votingId: this.votingId, lang: this.language, voteCode: voteCode});
        } else {
            request = Promise.reject(new Error("Invalid Action"));
        }

        await schedule(this.performRequest(request))
            .as("action-request");
    }

    private async performRequest(request: Promise<DOIResponse>): Promise<void> {
        try {
            const response: DOIResponse = await this.spinner.spinWhile(request);

            if (response.success) {
                this.actionState = this.action;
            } else if (response.errorId === "deadline-error") {
                this.actionState = response.errorId;
            } else {
                this.actionState = "error";
            }
        } catch (error) {
            EOP_ERROR_HANDLER(error, () => this.actionState = "error");
        }
    }
}
